import { NgModule } from '@angular/core';
import { RouterModule, type Routes } from '@angular/router';
import { IsManagementGuard } from '@principle-theorem/ng-principle-shared';
import { ManagementPagesModule } from './management-pages.module';
import { OrgRoleResolver } from './org-role-resolver.service';
import { OrgUserEditComponent } from './org-user-edit/org-user-edit.component';
import { OrgUserResolver } from './org-user-resolver.service';
import { OrganisationEditComponent } from './organisation-edit/organisation-edit.component';
import { OrganisationResolver } from './organisation-resolver.service';
import { OrganisationsComponent } from './organisations/organisations.component';
import { PartnerEditComponent } from './partner-edit/partner-edit.component';
import { PartnerResolver } from './partner-resolver.service';
import { PartnerUserEditComponent } from './partner-user-edit/partner-user-edit.component';
import { PartnerUserListComponent } from './partner-user-list/partner-user-list.component';
import { PartnerUserResolver } from './partner-user-resolver.service';
import { PartnersComponent } from './partners/partners.component';
import { RoleEditComponent } from './role-edit/role-edit.component';
import { RoleListComponent } from './role-list/role-list.component';
import { ToolsComponent } from './tools/tools.component';
import { UserListComponent } from './user-list/user-list.component';

export const MANAGEMENT_PAGES_ROUTES: Routes = [
  {
    path: '',
    redirectTo: 'organisations',
    pathMatch: 'full',
  },
  {
    path: 'organisations',
    children: [
      {
        path: '',
        component: OrganisationsComponent,
      },
      {
        path: ':uid',
        resolve: {
          organisation: OrganisationResolver,
        },
        children: [
          {
            path: '',
            component: OrganisationEditComponent,
          },
          {
            path: 'users',
            children: [
              {
                path: '',
                component: UserListComponent,
              },
              {
                path: ':uid',
                resolve: {
                  user: OrgUserResolver,
                },
                children: [
                  {
                    path: '',
                    component: OrgUserEditComponent,
                  },
                ],
              },
            ],
          },
          {
            path: 'roles',
            children: [
              {
                path: '',
                component: RoleListComponent,
              },
              {
                path: ':uid',
                resolve: {
                  role: OrgRoleResolver,
                },
                children: [
                  {
                    path: '',
                    component: RoleEditComponent,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'provisioning',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './provisioning/provisioning-list/provisioning-list.component'
          ).then((mod) => mod.ProvisioningListComponent),
      },
      {
        path: ':taskId',
        loadComponent: () =>
          import(
            './provisioning/provisioning-detail/provisioning-detail.component'
          ).then((mod) => mod.ProvisioningDetailComponent),
      },
    ],
  },
  {
    path: 'setup-wizards',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './setup-wizards/setup-wizard-list/setup-wizard-list.component'
          ).then((mod) => mod.SetupWizardListComponent),
      },
      {
        path: ':wizardId',
        loadComponent: () =>
          import(
            './setup-wizards/setup-wizard-detail/setup-wizard-detail.component'
          ).then((mod) => mod.SetupWizardDetailComponent),
      },
    ],
  },
  {
    path: 'partners',
    children: [
      {
        path: '',
        component: PartnersComponent,
      },
      {
        path: ':uid',
        resolve: {
          partner: PartnerResolver,
        },
        children: [
          {
            path: '',
            component: PartnerEditComponent,
          },
          {
            path: 'users',
            children: [
              {
                path: '',
                component: PartnerUserListComponent,
              },
              {
                path: ':uid',
                resolve: {
                  user: PartnerUserResolver,
                },
                children: [
                  {
                    path: '',
                    component: PartnerUserEditComponent,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'migrations',
    loadChildren: () =>
      import('@principle-theorem/ng-practice-migrations').then(
        (mod) => mod.PracticeMigrationsPagesRoutingModule
      ),
  },
  {
    path: 'tools',
    canActivate: [IsManagementGuard],
    children: [
      {
        path: '',
        component: ToolsComponent,
      },
      {
        path: 'reporting',
        loadComponent: () =>
          import('./tools/reporting/reporting-tests.component').then(
            (mod) => mod.ReportingTestsComponent
          ),
      },
      {
        path: 'auth-user-lookup',
        loadComponent: () =>
          import('./tools/auth-user-lookup/auth-user-lookup.component').then(
            (mod) => mod.AuthUserLookupComponent
          ),
      },
      {
        path: 'ai-scribe',
        loadComponent: () =>
          import(
            './tools/ai-scribe-tool-page/ai-scribe-tool-page.component'
          ).then((mod) => mod.AiScribeToolPageComponent),
      },
      {
        path: 'big-query-uploads',
        loadComponent: () =>
          import('./big-query-uploads/big-query-uploads.component').then(
            (mod) => mod.BigQueryUploadsComponent
          ),
        children: [
          {
            path: 'stats',
            loadComponent: () =>
              import(
                './big-query-uploads/big-query-upload-stats/big-query-upload-stats.component'
              ).then((mod) => mod.BigQueryUploadStatsComponent),
          },
          {
            path: 'jobs',
            loadComponent: () =>
              import(
                './big-query-uploads/big-query-upload-jobs/big-query-upload-jobs.component'
              ).then((mod) => mod.BigQueryUploadJobsComponent),
          },
          { path: '', redirectTo: 'stats', pathMatch: 'full' },
        ],
      },
      {
        path: 'typesense',
        loadComponent: () =>
          import('./tools/typesense/typesense.component').then(
            (mod) => mod.TypesenseComponent
          ),
      },
      {
        path: 'migration-runner',
        loadComponent: () =>
          import(
            './tools/migration-runner-page/migration-runner-page.component'
          ).then((mod) => mod.MigrationRunnerPageComponent),
      },
      {
        path: 'big-query-jobs',
        loadComponent: () =>
          import('./tools/big-query-jobs/big-query-jobs-page.component').then(
            (mod) => mod.BigQueryJobsPageComponent
          ),
      },
      {
        path: 'seed-demo-space',
        loadComponent: () =>
          import(
            './tools/seed-demo-space-page/seed-demo-space-page.component'
          ).then((mod) => mod.SeedDemoSpacePageComponent),
      },
      {
        path: 'revoke-auth-sessions',
        loadComponent: () =>
          import(
            './tools/revoke-auth-sessions/revoke-auth-sessions.component'
          ).then((mod) => mod.RevokeAuthSessionsComponent),
      },
      {
        path: 'api-log',
        loadComponent: () =>
          import(
            './tools/management-api-log/management-api-log.component'
          ).then((mod) => mod.ManagementApiLogComponent),
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forChild(MANAGEMENT_PAGES_ROUTES),
    ManagementPagesModule,
  ],
  exports: [RouterModule],
  providers: [
    OrganisationResolver,
    OrgUserResolver,
    OrgRoleResolver,
    PartnerUserResolver,
    PartnerResolver,
  ],
})
export class ManagementPagesRoutingModule {}
